/**
 * LinkedIn OAuth Service - Production Ready
 * Handles LinkedIn authentication flow
 */

import { API_URL } from '../config';

// Fallback credentials (production-level with env priority)
const FALLBACK_CLIENT_ID = '86mmak9ck036g3';

// Get LinkedIn Client ID (env first, then fallback)
const getLinkedInClientId = () => {
  return process.env.REACT_APP_LINKEDIN_CLIENT_ID || FALLBACK_CLIENT_ID;
};

/**
 * Get LinkedIn authorization URL
 * @param {string} role - User role ('candidate' or 'hr')
 * @returns {string} LinkedIn authorization URL
 */
export const getLinkedInAuthUrl = (role = 'candidate') => {
  const clientId = getLinkedInClientId();
  const redirectUri = `${window.location.origin}/auth/linkedin/callback`;
  const state = `${role}_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  
  console.log('🔗 LinkedIn OAuth Flow Starting:');
  console.log('   Redirect URI:', redirectUri);
  console.log('   Client ID:', clientId);
  
  // Store state in sessionStorage for verification
  sessionStorage.setItem('linkedin_oauth_state', state);
  sessionStorage.setItem('linkedin_oauth_role', role);
  
  const scope = 'openid profile email';
  
  const authUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
    `response_type=code&` +
    `client_id=${clientId}&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `state=${state}&` +
    `scope=${encodeURIComponent(scope)}`;
  
  return authUrl;
};

/**
 * Initiate LinkedIn OAuth flow
 * @param {string} role - User role ('candidate' or 'hr')
 */
export const initiateLinkedInAuth = (role = 'candidate') => {
  const authUrl = getLinkedInAuthUrl(role);
  window.location.href = authUrl;
};

/**
 * Handle LinkedIn OAuth callback
 * @param {string} code - Authorization code from LinkedIn
 * @param {string} state - State parameter for CSRF protection
 * @param {string} role - User role ('candidate' or 'hr')
 * @returns {Promise<Object>} User session data
 */
export const handleLinkedInCallback = async (code, state, role) => {
  try {
    // Verify state to prevent CSRF attacks
    const storedState = sessionStorage.getItem('linkedin_oauth_state');
    const storedRole = sessionStorage.getItem('linkedin_oauth_role');
    
    if (state !== storedState) {
      throw new Error('Invalid state parameter. Possible CSRF attack.');
    }
    
    // Use stored role if not provided
    const userRole = role || storedRole || 'candidate';
    
    // Clear stored state
    sessionStorage.removeItem('linkedin_oauth_state');
    sessionStorage.removeItem('linkedin_oauth_role');
    
    // Send code to backend
    const redirectUri = `${window.location.origin}/auth/linkedin/callback`;
    
    console.log('🔄 Sending to backend:');
    console.log('   Code:', code.substring(0, 20) + '...');
    console.log('   Redirect URI:', redirectUri);
    
    const response = await fetch(`${API_URL}/auth/linkedin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        role: userRole,
        redirect_uri: redirectUri,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'LinkedIn authentication failed');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('LinkedIn callback error:', error);
    throw error;
  }
};

/**
 * Authenticate with LinkedIn (direct code exchange)
 * @param {string} code - Authorization code from LinkedIn
 * @param {string} role - User role ('candidate' or 'hr')
 * @returns {Promise<Object>} User session data
 */
export const linkedinAuth = async (code, role = 'candidate') => {
  try {
    const response = await fetch(`${API_URL}/auth/linkedin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        role,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'LinkedIn authentication failed');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('LinkedIn auth error:', error);
    throw error;
  }
};

/**
 * Check if LinkedIn OAuth is configured
 * @returns {boolean} True if configured
 */
export const isLinkedInConfigured = () => {
  const clientId = getLinkedInClientId();
  return !!clientId && clientId !== 'your-linkedin-client-id';
};

/**
 * Get LinkedIn Client ID (for debugging)
 * @returns {string} Client ID
 */
export const getClientId = () => {
  return getLinkedInClientId();
};

export default {
  getLinkedInAuthUrl,
  initiateLinkedInAuth,
  handleLinkedInCallback,
  linkedinAuth,
  isLinkedInConfigured,
  getClientId,
};
